import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  authenticateTecgymAdmin,
  isAdminAuthenticated,
  logoutAdmin,
  saveServerAdminSession
} from "../libraries/auth";
import { showToast } from "../libraries/clipboardUtils";
import {
  getDefaultAccessibleRoute,
  canAccessNavRoute,
  resolveClientStatusFilter
} from "../libraries/privileges";

const getPostLoginRoute = () => {
  const route = getDefaultAccessibleRoute();

  if (route === "/branches") {
    return `/branches?filter=${encodeURIComponent(resolveClientStatusFilter())}`;
  }

  return route;
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedRoute = location.state?.from;
  const redirectTo = requestedRoute && canAccessNavRoute(requestedRoute)
    ? (requestedRoute === "/branches"
      ? `/branches?filter=${encodeURIComponent(resolveClientStatusFilter())}`
      : requestedRoute)
    : getPostLoginRoute();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo]);

  useEffect(() => {
    if (location.state?.sessionExpired) {
      showToast("Session expired. Please log in again.", "error");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const loginId = username.trim().toLowerCase();
    const trimmedPassword = password.trim();

    try {
      const authResult = await authenticateTecgymAdmin(loginId, trimmedPassword);

      if (authResult.ok) {
        logoutAdmin();
        saveServerAdminSession(authResult);
        navigate(getPostLoginRoute(), { replace: true });
        return;
      }

      if (authResult.reason === "network_error") {
        showToast(authResult.message || "Unable to reach authentication server.", "error");
        return;
      }

      showToast(authResult.message || "Invalid login credentials", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="loginPage">
      <section className="loginHero">
        <div className="loginBadgeWrap">
          <span className="loginBadge">Admin Portal</span>
        </div>
        <h1>TecGym Admin</h1>
        <p>Sign in to manage branches, onboarding, licenses, packages, and client activity.</p>
      </section>

      <section className="loginCard" aria-label="Admin login form">
        <div className="loginCardHeader">
          <span>Welcome back</span>
          <h2>Admin Login</h2>
        </div>

        <form onSubmit={handleSubmit} autoComplete="on">
          <label htmlFor="admin-username">Login ID</label>
          <input
            id="admin-username"
            name="username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Enter login ID"
            autoComplete="username"
            required
          />

          <label htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            required
          />

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Signing in..." : "Login"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default Login;
